import { useMemo } from 'react';
import { PlayablePlaybackOption } from '../../queries/ats/titles/types/playback-option';
import { useSearchParams } from '../../routes/utils/use-search-params';

export type RecordingType = 'series' | 'program';

/**
 * The context for the input parameters of the details page.
 * This context is used to determine the source to focus on the details page.
 * - `platformTitleId`: The ID of the platform title to focus on.
 * - `orderByProviderId`: The ID of the provider to order by. This is used to fetch the related content.
 * - `orderByServiceType`: The type of service to order by. This is used to fetch the related content.
 */
export interface InputContext {
  platformTitleId: string | undefined;
  orderByProviderId: string | undefined;
  orderByServiceType: PlayablePlaybackOption['serviceType'] | undefined;
}

export function useInputContext(): InputContext {
  const search = useSearchParams();

  return {
    platformTitleId: search.get('platformTitleId') || undefined,
    orderByProviderId: search.get('orderByProviderId') || undefined,
    orderByServiceType: useMemo(() => {
      const query = search.get('orderByServiceType');

      if (query !== undefined && validateServiceType(query)) {
        return query;
      }

      return undefined;
    }, [search]),
  };
}

function validateServiceType(input: string): input is PlayablePlaybackOption['serviceType'] {
  return ['svod', 'vod', 'thirdparty', 'program'].includes(input);
}
